import { useMemo, useState } from "react";
import { RotateCcw, TrashIcon } from "lucide-react";
import { useForm } from "react-hook-form";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { ToastMessage } from "../../../Components/Utils";
import { Button } from "../../../Components/Buttons";
import { FormSwitch, FormInput, FormSelect } from "../../../Components/Form";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchTalukasQuery,
  useFetchTrashedTalukasQuery,
  useCreateTalukaMutation,
  useUpdateTalukaMutation,
  useDeleteTalukaMutation,
  useDestroyTalukaMutation,
  useRestoreTalukaMutation,
  useBulkDeleteTalukasMutation,
  useBulkDestroyTalukasMutation,
  useBulkRestoreTalukasMutation,
} from "../../../stores/api/taluka.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";
import talukaHelper from "../../../stores/api/helpers/taluka.helper";
import { useFetchAllDistrictsQuery } from "../../../stores/api/district.api";
// import Dropdown from "../../User/components/Dropdown";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const HEADERS = [
  // { key: "id", label: "#", sortable: false },
  { key: "name", label: "Name", sortable: true },
  { key: "district.name", label: "District", sortable: true },
  {
    key: "status",
    label: "Status",
    type: "status",
  },
];

const TalukaForm = ({ defaultName = "", defaultStatus = "Active", defaultDistrictId = "", onSave, onCancel }) => {
  const { data: districtRows = [] } = useFetchAllDistrictsQuery();
  const districts = useMemo(
    () => (districtRows || []).map((r) => ({ label: r.name, value: r.id })),
    [districtRows]
  );

  const { register, handleSubmit, watch, setValue } = useForm({
    defaultValues: { name: defaultName, status: defaultStatus, district_id: defaultDistrictId },
  });
  const status = watch("status");
  return (

    <form
      onSubmit={handleSubmit(onSave)}
      onKeyDown={(e) => e.key === "Escape" && onCancel()}
      className="w-full grid grid-cols-1 md:grid-cols-[2fr_2fr_120px_auto] gap-3 items-end"
    >
      {/* District */}
      <div className="h-full flex flex-col justify-end">
        <FormSelect
          label="District"
          isRequired
          options={districts}
          value={watch("district_id")}
          onChange={(val) => {
            setValue("district_id", val);
          }}
          blurInputOnSelect
        />

        <input type="hidden" {...register("district_id")} />
      </div>

      {/* Taluka */}
      <div className="h-full flex flex-col justify-end">
        <FormInput
          label="Taluka Name"
          name="name"
          value={watch("name")}
          onChange={(val) => setValue("name", val)}
          placeholder="e.g. Manager"
          autoFocus
        />
      </div>

      {/* Status */}
      <div className="h-full flex flex-col justify-end">
        <FormSwitch
          checked={status === "Active" || status === true}
          onChange={(val) =>
            setValue("status", val, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
        <input type="hidden" {...register("status")} />
      </div>

      {/* Buttons */}
      <div className="h-full flex items-end gap-2">
        <button
          type="submit"
          className="px-4 py-1.5 text-xs font-semibold rounded-md bg-green-500 text-white"
        >
          Save
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-1.5 text-xs font-semibold rounded-md bg-gray-100"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const TalukasList = () => {
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig?.key?.toLowerCase(),
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchTalukasQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedTalukasQuery(queryParams, { skip: !trashed });
  const { data: TalukasData = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [createTaluka] = useCreateTalukaMutation();
  const [updateTaluka] = useUpdateTalukaMutation();
  const [deleteTaluka] = useDeleteTalukaMutation();
  const [destroyTaluka] = useDestroyTalukaMutation();
  const [restoreTaluka] = useRestoreTalukaMutation();
  const [bulkDeleteTalukas] = useBulkDeleteTalukasMutation();
  const [bulkDestroyTalukas] = useBulkDestroyTalukasMutation();
  const [bulkRestoreTalukas] = useBulkRestoreTalukasMutation();

  const resetPage = () => setPage(1);
  const handleCreateSave = async ({ name, district_id, status }) => {
    try {
      const payload = {
        name: name?.trim() ?? "",
        district_id: String(district_id ?? ""),
        status: Boolean(status),
      };

      await createTaluka(
        talukaHelper.forCreate(payload)
      ).unwrap();

      ToastMessage({
        type: "success",
        title: "Created!",
        message: `Taluka "${payload.name}" created.`,
      });
    } catch (err) {
      handleError(err, "Failed to create Taluka.");
    }
  };
  const handleEditSave = async ({ district_id, name, status }, row) => {
    try {
      const payload = {
        name: name?.trim() ?? "",
        district_id: String(district_id ?? ""),
        status: Boolean(status),
      };

      await updateTaluka(
        talukaHelper.forUpdate(row.id, payload)
      ).unwrap();

      ToastMessage({
        type: "success",
        title: "Updated!",
        message: `Taluka "${payload.name}" updated.`,
      });
    } catch (err) {
      handleError(err, "Failed to update Taluka.");
    }
  };

  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: `${verb} Taluka`,
      message: `${verb} "${row.name}"?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") {
            await restoreTaluka(row.id).unwrap();
          } else if (type === "destroy") {
            await destroyTaluka(row.id).unwrap();
          } else {
            await deleteTaluka(row.id).unwrap();
          }
          ToastMessage({ type: "success", title: "Done!", message: `Taluka ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed.");
        }
      },
    });
  };

  const handleBulk = (selectedItems, type = "delete") => {
    const ids = selectedItems;
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: "Bulk Action",
      message: `${verb} ${ids.length} Taluka(s)?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(ids);
          if (type === "restore") {
            await bulkRestoreTalukas(body).unwrap();
          } else if (type === "destroy") {
            await bulkDestroyTalukas(body).unwrap();
          } else {
            await bulkDeleteTalukas(body).unwrap();
          }

          ToastMessage({ type: "success", title: "Done!", message: `${ids.length} Taluka(s) ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  return (
    <>
      <BreadCrumb title="Taluka" />
      <TableListing
        headers={HEADERS}
        paginationData={TalukasData}
        loading={isFetching}
        handleBulk={handleBulk}
        search={search}
        setSearch={setSearch}
        statusModule="taluka"
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
        inlineEdit={
          !trashed
            ? (row, _onSave, onCancel) => (
              <TalukaForm
                defaultName={row.name}
                defaultStatus={row.status ?? "Active"}
                defaultDistrictId={row.district_id ?? ""}
                onSave={(data) => {
                  handleEditSave(data, row);
                  onCancel();
                }}
                onCancel={onCancel}
              />
            )
            : null
        }
        inlineCreate={
          !trashed
            ? (_onSave, onCancel) => (
              <TalukaForm
                onSave={(data) => {
                  handleCreateSave(data);
                  onCancel();
                }}
                onCancel={onCancel}
              />
            )
            : null
        }
        onInlineEditSave={() => { }}
        onInlineCreateSave={() => { }}
        actions={(row) =>
          trashed ? (
            <div className="flex gap-2">
              <Button
                onClick={() => handleSingle(row, "restore")}
                title="Restore"
                icon={RotateCcw}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
              <Button
                onClick={() => handleSingle(row, "destroy")}
                title="Destroy"
                icon={TrashIcon}
                className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
              />
            </div>
          ) : (
            <Button
              onClick={() => handleSingle(row, "delete")}
              title="Delete"
              icon={TrashIcon}
              className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
            />
          )
        }
      />
    </>
  );
};

export default TalukasList;
